/**
 * Aplica los tokens de diseño que manda el anfitrión (ítem 038 de la suite).
 *
 * El contrato está en Unity, `docs/CONTRATO-DISENO-SUITE.md`. Lo que hay que
 * saber para leer este archivo:
 *
 * ── POR QUÉ LA RAMPA ENTERA Y NO TRES COLORES ───────────────────────────────
 *
 * Hasta el 037 llegaban tres colores y `index.css` traducía a mano los
 * utilitarios que usaban las pantallas embebidas: ocho reglas. Finance tiene
 * **1.779 usos de `blue-*`**, así que la pantalla siguiente traía un
 * `bg-blue-100` que nadie había mapeado y volvía a verse prestada — sin que
 * nada fallara ni avisara.
 *
 * Ahora llegan los diez pasos y los consume `tailwind.config.js`: la rampa de
 * marca entera pasa por estas variables. **Toda pantalla queda cubierta, hoy y
 * la que se escriba el año que viene.** No hay lista que mantener.
 *
 * ── POR QUÉ CANALES Y NO HEXADECIMAL ───────────────────────────────────────
 *
 * Por el cable viaja `#0077b6`, que es legible. Acá se convierte a `0 119 182`
 * porque es lo que Tailwind necesita para que `bg-blue-50/50` siga funcionando,
 * y eso se usa en 11 lugares de Finance. Si la variable guardara el
 * hexadecimal, esos `/50` producirían un color inválido y el fondo
 * desaparecería.
 *
 * ── POR QUÉ LISTA BLANCA Y VALIDACIÓN ──────────────────────────────────────
 *
 * Un valor que llega por `postMessage` entra al CSS de la página. No puede ser
 * cualquier cosa: solo se aceptan los nombres de esta lista, y solo valores que
 * no puedan cerrar una declaración CSS.
 */

/** Los diez pasos de la rampa, los mismos que Unity manda. */
const PASOS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

/**
 * Los neutros y la tipografía, y a qué variable CSS va cada uno.
 *
 * Estos se usan directamente —en `index.css` y en algún `style` de la cáscara—,
 * así que van en hexadecimal tal como llegan.
 */
const NEUTROS = {
  texto: '--embed-texto',
  fondo: '--embed-fondo',
  borde: '--embed-borde',
  tipografia: '--embed-tipografia',
  // Los tres del 037. Siguen entrando porque un despliegue de Unity anterior al
  // de Finance seguiría mandándolos solos, y en ese rato la pantalla igual tiene
  // que verse bien.
  primario: '--embed-primario',
  primarioOscuro: '--embed-primario-oscuro',
  primarioSuave: '--embed-primario-suave'
};

/** Un color o una familia tipográfica; nada que pueda cerrar una declaración CSS. */
const VALOR_SEGURO = /^[#a-zA-Z0-9\s,.()%'"-]{1,120}$/;

/**
 * `#0077b6` → `0 119 182`. Devuelve null si no es un hexadecimal de color, para
 * que un valor raro deje la variable sin tocar en vez de romper la regla CSS.
 */
export function aCanales (hex) {
  if (typeof hex !== 'string') return null;
  const limpio = hex.trim().replace(/^#/, '');
  const seis = limpio.length === 3
    ? limpio.split('').map((c) => c + c).join('')
    : limpio;
  if (!/^[0-9a-fA-F]{6}$/.test(seis)) return null;
  const n = (desde) => parseInt(seis.slice(desde, desde + 2), 16);
  return `${n(0)} ${n(2)} ${n(4)}`;
}

/**
 * Pone los tokens del anfitrión como variables CSS en la raíz del documento.
 *
 * Tolera lo que no entiende: un tema a medias aplica lo que trae y deja el resto
 * en su valor de reserva. Eso es a propósito — los dos productos se despliegan
 * por separado y durante un rato uno de los dos lados va a ser más viejo.
 */
export function aplicarTemaEmbebido (tema) {
  if (!tema || typeof tema !== 'object') return;
  const raiz = document.documentElement;

  for (const [nombre, variable] of Object.entries(NEUTROS)) {
    const valor = tema[nombre];
    if (typeof valor === 'string' && VALOR_SEGURO.test(valor)) {
      raiz.style.setProperty(variable, valor);
    }
  }

  const marca = tema.marca;
  if (!marca || typeof marca !== 'object') return;
  for (const paso of PASOS) {
    const canales = aCanales(marca[paso]);
    if (canales) raiz.style.setProperty(`--embed-marca-${paso}`, canales);
  }
}
